import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';

export type OnboardingStep = 'plan' | 'website';

export type OnboardingState = {
  /** Whether the account must finish setting up before it sees the dashboard. */
  required: boolean;
  step: OnboardingStep | null;
  isAgency: boolean;
  /** False for a Member: choosing a plan and adding websites are Owner and Admin actions. */
  canAct: boolean;
};

export const ONBOARDING_KEY = ['ONBOARDING'] as const;

/**
 * Whether this account still has setting up to do (`GET /api/onboarding`).
 *
 * Never refetched on its own. The website step ends on the snippet screen, but the
 * server counts the account as set up the moment its website exists, so a refetch
 * when the tab regains focus (which is exactly what happens when someone goes to
 * paste the snippet into their site and comes back) would drop them out of
 * onboarding before they had copied it. It is refetched when a step is done, and when
 * a plan checkout is confirmed.
 */
export const useOnboarding = () => {
  const apiClient = useClient();
  const { user } = useAuth();

  const { data, isLoading, isError, refetch } = useQuery<OnboardingState>({
    // Scoped to the person and the account: the answer is theirs, and a sign-out in the
    // same tab (a client-side navigation that leaves the query cache alone) must not hand
    // the next person the previous one's verdict.
    queryKey: [...ONBOARDING_KEY, user?.id, user?.companyId],
    enabled: !!user,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // A Member waits for an Owner or Admin to finish setting up, and nothing else would
    // tell their open tab. Slow, and only while they are waiting.
    refetchInterval: (query) => (query.state.data?.required && !query.state.data.canAct ? 30_000 : false),
    queryFn: () => apiClient.get('/onboarding'),
  });

  return { onboarding: data, isLoading, isError, refetch };
};
